import {
  type ListDeliveryEntriesParams,
  activateDelivery,
  cancelDelivery,
  completeDelivery,
  createDelivery,
  createDeliveryEntry,
  fetchDeliveries,
  fetchDelivery,
  fetchDeliveryCompletionPreview,
  fetchDeliveryEntries,
  fetchDeliveryEntryOutputOrphans,
  fetchDeliveryEntryTask,
  fetchDeliveryMetadataBinding,
  fetchDeliverySummary,
  fetchEntryOutputs,
  fetchMetadataSources,
  putDeliveryMetadataBinding,
  rebuildDeliveryEntry,
  setDeliveryCustomerSyncStatus,
  updateMcapBuildConfig,
  voidDeliveryEntry,
} from '@/features/delivery/api/deliveries';
import {
  DELIVERIES_PAGE_SIZE,
  DELIVERY_ENTRIES_PAGE_SIZE,
  DELIVERY_QUERY_KEY,
  METADATA_SOURCES_STALE_TIME,
  OUTPUT_BUILD_POLL_MS,
  SOURCE_ORPHANS_LIMIT,
} from '@/features/delivery/config/constants';
import { deliveryKey, entriesKey, summaryKey } from '@/features/delivery/config/deliveryQueryKeys';
import {
  type CreateDeliveryInput,
  type CreateEntryInput,
  type CustomerSyncStatus,
  type Delivery,
  type PutMetadataBindingInput,
  type RebuildEntryInput,
  type UpdateMcapBuildConfigInput,
  isActiveTaskState,
} from '@/features/delivery/types/contracts';
import { ApiHttpError } from '@/shared/api/client';
import {
  type QueryClient,
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { invalidateDeliveryAggregate } from './deliveryQueryInvalidation';
import { invalidateOutput, outputConfigKey } from './outputQueries';

const listKey = (term: string) => [DELIVERY_QUERY_KEY, 'list', term] as const;
const filterOptionsKey = [DELIVERY_QUERY_KEY, 'filter-options'] as const;
const completionPreviewKey = (id: string) =>
  [DELIVERY_QUERY_KEY, 'completion-preview', id] as const;
const rejectedActiveEntryCheckKey = (id: string) =>
  [...entriesKey(id), 'rejected-active-check'] as const;
const outputsKey = (entryId: string) => [DELIVERY_QUERY_KEY, 'outputs', entryId] as const;
const entryTaskKey = (entryId: string) => [DELIVERY_QUERY_KEY, 'entry-task', entryId] as const;
const entryOrphansKey = (id: string) => [DELIVERY_QUERY_KEY, 'entry-output-orphans', id] as const;
const metadataBindingKey = (id: string) => [DELIVERY_QUERY_KEY, 'metadata-binding', id] as const;
/**
 * The source catalog is delivery-independent (it is the platform's vocabulary), so
 * it is keyed once under this namespace rather than per delivery — every binding
 * editor shares the one cache entry.
 */
const metadataSourcesKey = [DELIVERY_QUERY_KEY, 'metadata-sources'] as const;

async function nullOnNotFound<T>(request: () => Promise<T>): Promise<T | null> {
  try {
    return await request();
  } catch (error) {
    if (error instanceof ApiHttpError && error.status === 404) return null;
    throw error;
  }
}

/**
 * A source void (including Delivery cancellation) cascades into its Output Entry:
 * ready/pending rows become `blocked/source_voided`, while a building task is cancelled and
 * reaches that state during terminal reconciliation. Output query keys are output-scoped rather
 * than delivery-scoped, so resolve the Delivery's cached Output ids and invalidate only those
 * Output prefixes after the cascade.
 */
function invalidateOutputCascade(qc: QueryClient, deliveryId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: outputConfigKey(deliveryId) }),
    invalidateOutput(qc, deliveryId),
  ]);
}

function invalidateVoidedSources(qc: QueryClient, deliveryId: string) {
  return Promise.all([
    invalidateDeliveryAggregate(qc, deliveryId),
    invalidateOutputCascade(qc, deliveryId),
  ]);
}

// --- Reads ---

/**
 * Keyset-paginated list of deliveries (`GET /deliveries`). `.manage`-gated on the
 * backend, so `enabled` should be the caller's `.manage` grant — the query stays
 * idle (never hits the endpoint) for non-admins, who keep the local recent-views
 * console instead. Paged by `nextCursor`; `enabled` also lets the console mount
 * the admin list only when authorized.
 *
 * `q` narrows the same ordering by the delivery's display **name** (see
 * `fetchDeliveries`). It is trimmed and folded into the query key so each settled
 * term caches its own pages, and a blank term shares the unfiltered list's cache.
 */
function deliveriesInfiniteQueryOptions(enabled: boolean, q = '') {
  const term = q.trim();
  return infiniteQueryOptions({
    queryKey: listKey(term),
    queryFn: ({ pageParam, signal }) =>
      fetchDeliveries(
        {
          limit: DELIVERIES_PAGE_SIZE,
          cursor: pageParam,
          ...(term ? { q: term } : {}),
        },
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    // Keep the previous term's rows visible (dimmed as `stale`) while the new
    // term's first page loads, so typing narrows a list instead of clearing it.
    placeholderData: keepPreviousData,
  });
}

/**
 * No `throwOnError`: this list is sidebar chrome mounted beside the section `<Outlet/>`, so
 * escalating would replace the page the user is actually reading with an error screen because a
 * rail failed. The component owns the inline retry UI.
 */
export function useDeliveries(enabled: boolean, q = '') {
  return useInfiniteQuery(deliveriesInfiniteQueryOptions(enabled, q));
}

/** Complete Delivery option set for catalog filters. Enabled only after a manage permission check. */
export function useDeliveryFilterOptions(enabled: boolean) {
  return useQuery({
    queryKey: filterOptionsKey,
    enabled,
    queryFn: async ({ signal }) => {
      const items: Delivery[] = [];
      let cursor: string | null = null;
      do {
        const page = await fetchDeliveries({ limit: 500, cursor }, { signal });
        items.push(...page.items);
        cursor = page.nextCursor ?? null;
      } while (cursor);
      return items;
    },
  });
}

/**
 * Shared options for a single delivery query, so the route loader can prefetch the exact same
 * query {@link DeliveryDetail} reads — letting `$deliveryId` own its loading skeleton +
 * initial-fetch error UI at the route level.
 */
function deliveryQueryBase(deliveryId: string) {
  return {
    queryKey: deliveryKey(deliveryId),
    queryFn: ({ signal }: { signal: AbortSignal }) => fetchDelivery(deliveryId, { signal }),
  };
}

export function deliveryQueryOptions(deliveryId: string) {
  return queryOptions({ ...deliveryQueryBase(deliveryId), enabled: !!deliveryId });
}

/**
 * For a caller that types the delivery id itself and must cope with it naming nothing
 * (see `AddSegmentToDeliveryDialog`): `enabled` keeps an empty box from fetching, and
 * a miss is a state the component renders rather than an error it throws.
 */
export function useDelivery(deliveryId: string) {
  return useQuery(deliveryQueryOptions(deliveryId));
}

/**
 * For the delivery *page*, whose route loader has already `ensureQueryData`'d this
 * exact query: the id came from a route param the loader resolved, so a missing
 * delivery is a load failure the route's `errorComponent` already owns. Suspending
 * here is what makes `data` non-optional, so the page needs no not-found branch of
 * its own for a case the backend cannot produce.
 */
export function useDeliverySuspense(deliveryId: string) {
  return useSuspenseQuery(queryOptions(deliveryQueryBase(deliveryId)));
}

export function useDeliverySummary(deliveryId: string) {
  return useQuery({
    queryKey: summaryKey(deliveryId),
    queryFn: ({ signal }) => fetchDeliverySummary(deliveryId, { signal }),
    enabled: !!deliveryId,
  });
}

export function useDeliveryCompletionPreview(deliveryId: string) {
  return useQuery({
    queryKey: completionPreviewKey(deliveryId),
    queryFn: ({ signal }) => fetchDeliveryCompletionPreview(deliveryId, { signal }),
    enabled: !!deliveryId,
  });
}

/** Which slice of a delivery's entries a caller wants. Every field narrows server-side. */
export type DeliveryEntriesFilters = Pick<
  ListDeliveryEntriesParams,
  'batchNo' | 'state' | 'buildState' | 'assetState' | 'q'
>;

/**
 * Keyset-paginated delivery entries (`GET /deliveries/:id/entries`), ordered by
 * `(batchNo, ordinal)`. Every filter (including the source Asset's current rejection
 * state) is applied by the BACKEND, so narrowing finds
 * an entry on a later cursor page too — and each filter tuple
 * caches its own pages while staying under the `entriesKey` prefix that mutations
 * invalidate.
 *
 * The filters are fail-closed server-side (an invalid one is a 400), so nothing
 * here may retry a rejected request with the parameter dropped — that would answer
 * a narrowed question with the unnarrowed listing.
 */
function deliveryEntriesInfiniteQueryOptions(
  deliveryId: string,
  { batchNo, state, buildState, assetState, q }: DeliveryEntriesFilters = {},
) {
  const term = q?.trim() ?? '';
  return infiniteQueryOptions({
    queryKey: [
      ...entriesKey(deliveryId),
      batchNo ?? null,
      state ?? null,
      buildState ?? null,
      assetState ?? null,
      term,
    ] as const,
    queryFn: ({ pageParam, signal }) =>
      fetchDeliveryEntries(
        deliveryId,
        {
          batchNo,
          state,
          buildState,
          assetState,
          ...(term ? { q: term } : {}),
          limit: DELIVERY_ENTRIES_PAGE_SIZE,
          cursor: pageParam,
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!deliveryId,
    // Keep the previous filter's rows on screen (dimmed as `stale`) while the new
    // one's first page loads, so narrowing narrows a list instead of clearing it.
    placeholderData: keepPreviousData,
  });
}

/** The paged entries query for list surfaces. */
export function useDeliveryEntries(deliveryId: string, filters: DeliveryEntriesFilters = {}) {
  return useInfiniteQuery(deliveryEntriesInfiniteQueryOptions(deliveryId, filters));
}

/**
 * Lightweight existence check for retained Entries whose source Asset is now rejected.
 * Only one full Entry projection is requested: the workspace needs a yes/no warning,
 * while the paginated cleanup list is loaded separately when the operator asks to see it.
 */
export function useHasRejectedActiveDeliveryEntries(deliveryId: string) {
  return useQuery({
    queryKey: rejectedActiveEntryCheckKey(deliveryId),
    queryFn: async ({ signal }) => {
      const page = await fetchDeliveryEntries(
        deliveryId,
        { state: 'active', assetState: 'rejected', limit: 1 },
        { signal },
      );
      return page.items.length > 0;
    },
    enabled: !!deliveryId,
  });
}

/**
 * The delivery's SOURCE-side orphaned-output report (admin-only, `.manage`). Read-only —
 * there is no purge counterpart, because the upload bucket grants no delete authority
 * (see `fetchDeliveryEntryOutputOrphans`), so no consumer may render a delete action.
 *
 * A plain `useQuery`, not an infinite one: the endpoint takes `?limit=` and returns no
 * cursor, so `SOURCE_ORPHANS_LIMIT` is the whole coverage bound rather than a page size.
 * No `enabled` gate beyond the id — the caller is a registry dialog, so mounting is the
 * gate.
 */
export function useDeliveryEntryOutputOrphans(deliveryId: string) {
  return useQuery({
    queryKey: entryOrphansKey(deliveryId),
    queryFn: ({ signal }) =>
      fetchDeliveryEntryOutputOrphans(deliveryId, { limit: SOURCE_ORPHANS_LIMIT }, { signal }),
    enabled: !!deliveryId,
  });
}

/** Entry outputs are read on demand — disabled until `requested` (e.g. the row is expanded). */
export function useEntryOutputs(entryId: string, requested: boolean) {
  return useQuery({
    queryKey: outputsKey(entryId),
    queryFn: ({ signal }) => fetchEntryOutputs(entryId, { signal }),
    enabled: requested && !!entryId,
  });
}

/**
 * Read a delivery entry's build task on demand — disabled until `requested`
 * (e.g. the details dialog is open). A `null` result (backend 404) is the normal
 * "no build task for the current generation yet" state, not an error. Polls
 * while the task is still active (pending/running) so a running build's progress
 * and eventual failure surface without a manual refresh; a settled task stops
 * polling.
 */
export function useDeliveryEntryTask(entryId: string, requested: boolean) {
  return useQuery({
    queryKey: entryTaskKey(entryId),
    queryFn: ({ signal }) => nullOnNotFound(() => fetchDeliveryEntryTask(entryId, { signal })),
    enabled: requested && !!entryId,
    refetchInterval: (query) =>
      query.state.data && isActiveTaskState(query.state.data.state) ? OUTPUT_BUILD_POLL_MS : false,
  });
}

/**
 * Read the delivery's ACTIVE metadata binding — the customer-facing field list the
 * metadata patch dialog edits against. A `null` result (backend 404) is the normal
 * "no binding yet" state, so `data === null` is a resolved value, not an error.
 *
 * No `requested` gate, unlike {@link useDeliveryEntryTask}: the caller is a registry
 * dialog, mounted only while it is open, so mounting IS the gate.
 */
export function useDeliveryMetadataBinding(deliveryId: string) {
  return useQuery({
    queryKey: metadataBindingKey(deliveryId),
    queryFn: ({ signal }) =>
      nullOnNotFound(() => fetchDeliveryMetadataBinding(deliveryId, { signal })),
    enabled: !!deliveryId,
  });
}

/**
 * The source-path catalog every metadata mapping draws from
 * (`GET /metadata/sources`). It is a compiled-in vocabulary on the backend — it
 * changes only when the platform ships a new field — so it holds a long
 * `staleTime` and is shared by every delivery's binding editor.
 *
 * No `enabled` gate: the only caller is the registry-mounted binding dialog, so
 * mounting is the gate (as with {@link useDeliveryMetadataBinding}).
 */
export function useMetadataSources() {
  return useQuery({
    queryKey: metadataSourcesKey,
    queryFn: ({ signal }) => fetchMetadataSources({ signal }),
    select: (response) => response.sources,
    staleTime: METADATA_SOURCES_STALE_TIME,
  });
}

// --- Metadata binding mutation ---

/**
 * Replace the delivery's metadata binding. Invalidates the binding read so the
 * new revision — and the schema the entry-level metadata form is driven by —
 * is refetched.
 *
 * It does NOT re-resolve already-baked output entry snapshots: those are frozen
 * copies, and re-baking them is a separate, explicit output action
 * (`useResolveOutputMetadata`). A new binding therefore changes what the *next*
 * resolve produces, not what is already in the output.
 */
export function usePutDeliveryMetadataBinding(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PutMetadataBindingInput) => putDeliveryMetadataBinding(deliveryId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: metadataBindingKey(deliveryId) }),
  });
}

// --- Customer sync status mutation ---

/** Operator-maintained marker, independent of Delivery lifecycle and rclone. */
export function useSetDeliveryCustomerSyncStatus(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: CustomerSyncStatus) => setDeliveryCustomerSyncStatus(deliveryId, status),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: deliveryKey(deliveryId) }),
        qc.invalidateQueries({ queryKey: [DELIVERY_QUERY_KEY, 'list'] }),
        qc.invalidateQueries({ queryKey: filterOptionsKey }),
      ]),
  });
}

// --- Delivery lifecycle mutations ---

export function useCancelDelivery(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => cancelDelivery(deliveryId, reason),
    onSuccess: () => invalidateVoidedSources(qc, deliveryId),
  });
}

export function useCompleteDelivery(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => completeDelivery(deliveryId),
    onError: (error) => {
      if (error instanceof ApiHttpError && error.status === 409) {
        return Promise.all([
          invalidateDeliveryAggregate(qc, deliveryId),
          qc.invalidateQueries({ queryKey: completionPreviewKey(deliveryId) }),
        ]);
      }
    },
    onSuccess: async () => {
      await Promise.all([
        invalidateDeliveryAggregate(qc, deliveryId),
        qc.invalidateQueries({ queryKey: completionPreviewKey(deliveryId) }),
      ]);
    },
  });
}

export function useUpdateMcapBuildConfig(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMcapBuildConfigInput) => updateMcapBuildConfig(deliveryId, input),
    onSuccess: () => invalidateDeliveryAggregate(qc, deliveryId),
  });
}

// --- Entry mutations ---
//
// There are no batch mutations. A batch is a derived grouping, so adding an entry
// creates its batch implicitly and nothing else creates one.

export function useCreateDeliveryEntry(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEntryInput) => createDeliveryEntry(deliveryId, input),
    onSuccess: () => invalidateDeliveryAggregate(qc, deliveryId),
  });
}

/** One segment to add, with a caller-reserved `entryId` (stable across retries → idempotent). */
export interface BulkEntryItem {
  segmentId: string;
  entryId: string;
}

/** Outcome of a bulk add: which segments landed and which failed (with the transport error). */
export interface BulkEntryResult {
  added: string[];
  failed: { segmentId: string; error: unknown }[];
}

/**
 * Adds many segments to a delivery. The delivery API still has no bulk endpoint, so
 * this POSTs each segment sequentially and collects partial failures rather than
 * throwing — the caller keeps the dialog open, drops the `added` segments, and can
 * retry the rest with the same (idempotent) `entryId`s.
 *
 * Placement is no longer this hook's business: the server assigns each entry's
 * `batchNo`/`ordinal` under the delivery row lock, so a failed item cannot leave a
 * gap or collide — no slot was ever allocated for it. Which batch the entries land
 * in is whatever the server decides, and a run may straddle two.
 */
export function useCreateDeliveryEntries(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { items: BulkEntryItem[] }): Promise<BulkEntryResult> => {
      const added: string[] = [];
      const failed: { segmentId: string; error: unknown }[] = [];
      for (const item of vars.items) {
        try {
          await createDeliveryEntry(deliveryId, {
            entryId: item.entryId,
            segmentId: item.segmentId,
          });
          added.push(item.segmentId);
        } catch (error) {
          failed.push({ segmentId: item.segmentId, error });
        }
      }
      return { added, failed };
    },
    onSuccess: () => invalidateDeliveryAggregate(qc, deliveryId),
  });
}

export function useRebuildDeliveryEntry(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { entryId: string; input: RebuildEntryInput }) =>
      rebuildDeliveryEntry(vars.entryId, vars.input),
    onSuccess: (_result, vars) =>
      Promise.all([
        invalidateDeliveryAggregate(qc, deliveryId),
        qc.invalidateQueries({ queryKey: outputsKey(vars.entryId) }),
        qc.invalidateQueries({ queryKey: entryTaskKey(vars.entryId) }),
      ]),
  });
}

export function useVoidDeliveryEntry(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { entryId: string; reason: string }) =>
      voidDeliveryEntry(vars.entryId, vars.reason),
    onSuccess: () => invalidateVoidedSources(qc, deliveryId),
  });
}

/**
 * Create a Delivery then immediately activate it. Used by the create form's
 * React 19 `useActionState` action (which calls this `services/` function
 * directly rather than a hook). The caller supplies the reserved `deliveryId`
 * so a retry after a lost response replays the same id idempotently.
 */
export async function createAndActivateDelivery(input: CreateDeliveryInput): Promise<Delivery> {
  await createDelivery(input);
  return activateDelivery(input.deliveryId);
}
